import {
  CRM_EINWILLIGUNG_FILTER, CRM_FILTER_PARAMETER, KONTOSTATUS_SHOPIFY,
  type CrmAmpel, type CrmFilterParameter, type CrmKpi, type CrmKundenFilterQuery,
} from '@portal/contracts';
import { CRM_PFAD } from './crm-felder.js';

/**
 * #839 KPI-Leiste + Listenfilter (reine Logik, ohne React — testbar). Der Filter lebt in der URL-Query von `/crm`
 * (teilbar, Zurück-Taste); die Parameter sind 1:1 die der API (`GET /api/crm/v1/kunden`), die Kachelwerte stammen aus
 * `GET /api/crm/v1/kpi` mit denselben SQL-Bedingungen — Kachelwert = Zeilenzahl der gefilterten Liste.
 */
export type CrmFilter = CrmKundenFilterQuery;

export const zahl = (n: number): string => n.toLocaleString('de-DE');
const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
const ZEIT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin' });

const DATUM_RE = /^\d{4}-\d{2}-\d{2}$/;
/** gültiges Kalenderdatum JJJJ-MM-TT (wie die API: kein 2026-02-30, kein 2026-13-01) — sonst würde die Anzeige werfen (Review #130 P2) */
export function istDatum(v: string): boolean {
  if (!DATUM_RE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}
const BETRAG_RE = /^\d{1,10}(\.\d{1,2})?$/;

/** URL-Query → Filter: nur bekannte Parameter mit plausiblem Wert (der Rest wird ignoriert; die API prüft endgültig) */
export function filterAusQuery(search: string): CrmFilter {
  const p = new URLSearchParams(search);
  const f: CrmFilter = {};
  const v = (k: CrmFilterParameter) => p.get(k) ?? '';
  if ((KONTOSTATUS_SHOPIFY as readonly string[]).includes(v('status'))) f.status = v('status') as CrmFilter['status'];
  if ((CRM_EINWILLIGUNG_FILTER as readonly string[]).includes(v('einwilligung'))) f.einwilligung = v('einwilligung') as CrmFilter['einwilligung'];
  if (v('blacklist') === 'true' || v('blacklist') === 'false') f.blacklist = v('blacklist') as 'true' | 'false';
  if (v('ohne_email') === 'true') f.ohne_email = 'true';
  if (istDatum(v('kunde_seit_ab'))) f.kunde_seit_ab = v('kunde_seit_ab');
  if (istDatum(v('letztkauf_ab'))) f.letztkauf_ab = v('letztkauf_ab');
  if (BETRAG_RE.test(v('umsatz_min'))) f.umsatz_min = v('umsatz_min');
  return f;
}

/** Filter → Query-String (feste Reihenfolge, damit gleiche Filter gleiche URLs ergeben) */
export function filterZuQuery(f: CrmFilter): string {
  const p = new URLSearchParams();
  for (const k of CRM_FILTER_PARAMETER) {
    const w = f[k];
    if (w !== undefined && w !== '') p.set(k, w);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const listenPfad = (f: CrmFilter): string => `${CRM_PFAD}${filterZuQuery(f)}`;

export const aktiveFilter = (f: CrmFilter): CrmFilterParameter[] => CRM_FILTER_PARAMETER.filter((k) => f[k] !== undefined && f[k] !== '');

/** Filter ohne einen Parameter (Chip „×“) */
export function ohne(f: CrmFilter, k: CrmFilterParameter): CrmFilter {
  const n = { ...f };
  delete n[k];
  return n;
}

const gleich = (a: CrmFilter, b: CrmFilter): boolean => filterZuQuery(a) === filterZuQuery(b);

const STATUS_LABEL: Record<string, string> = { ENABLED: 'aktiv', INVITED: 'eingeladen', DISABLED: 'deaktiviert', DECLINED: 'abgelehnt' };
const EINWILLIGUNG_LABEL: Record<string, string> = {
  gueltig: 'E-Mail-Einwilligung gültig',
  bestaetigt: 'E-Mail-Einwilligung bestätigt',
  einfach: 'E-Mail-Einwilligung einfach',
  widerrufen: 'E-Mail-Einwilligung widerrufen',
  keine: 'Ohne E-Mail-Einwilligung',
};
const datum = (iso: string) => (istDatum(iso) ? DATUM.format(new Date(`${iso}T00:00:00Z`)) : iso);

/** Text eines aktiven Filters (Chip) */
export function filterLabel(k: CrmFilterParameter, w: string): string {
  switch (k) {
    case 'status':
      return `Shopify-Konto ${STATUS_LABEL[w] ?? w}`;
    case 'einwilligung':
      return EINWILLIGUNG_LABEL[w] ?? w;
    case 'blacklist':
      return w === 'true' ? 'Blacklist' : 'Nicht auf der Blacklist';
    case 'ohne_email':
      return 'Ohne E-Mail';
    case 'kunde_seit_ab':
      return `Kunde seit ${datum(w)}`;
    case 'letztkauf_ab':
      return `Letztkauf seit ${datum(w)}`;
    case 'umsatz_min':
      return w === '0.01' ? 'Mit Umsatz' : `Umsatz ab ${EUR.format(Number(w))}`;
  }
}

/** Ein klickbarer Wert einer Kachel: Zahl + Text, Ziel = Liste mit Filter */
export interface KpiWert {
  label: string;
  wert: number;
  filter: CrmFilter;
}
export interface KpiKachel {
  id: string;
  titel: string;
  /** #851 kurzer Hinweis zur Zählweise (sichtbar unter den Werten, auch als title) */
  hinweis?: string;
  haupt: KpiWert;
  teil: KpiWert;
}

/** #851 „Kunde seit“ unbekannt bei NEO-Personen ohne Anlagedatum → zählen nicht als neu */
export const NEU_HINWEIS = 'ohne NEO-Personen ohne Anlagedatum';

/** Die fünf Zähl-Kacheln (Kachel 6 „Letzter Abgleich“ ist Information, nicht klickbar) */
export function kacheln(k: CrmKpi): KpiKachel[] {
  return [
    {
      id: 'personen', titel: 'Personen',
      haupt: { label: 'Personen', wert: k.personen, filter: {} },
      teil: { label: 'davon aktive Shopify-Konten', wert: k.aktiv_shopify, filter: { status: 'ENABLED' } },
    },
    {
      id: 'neu', titel: 'Neukunden', hinweis: NEU_HINWEIS,
      haupt: { label: 'neu in 30 Tagen', wert: k.neu_30t, filter: { kunde_seit_ab: k.grenzen.neu_30t } },
      teil: { label: 'neu in 12 Monaten', wert: k.neu_12m, filter: { kunde_seit_ab: k.grenzen.neu_12m } },
    },
    {
      id: 'umsatz', titel: 'Umsatz',
      haupt: { label: 'mit Umsatz', wert: k.mit_umsatz, filter: { umsatz_min: '0.01' } },
      teil: { label: 'Letztkauf in 12 Monaten', wert: k.letztkauf_12m, filter: { letztkauf_ab: k.grenzen.letztkauf_12m } },
    },
    {
      id: 'einwilligung', titel: 'E-Mail-Einwilligung',
      haupt: { label: 'gültig', wert: k.einwilligung_gueltig, filter: { einwilligung: 'gueltig' } },
      teil: { label: 'widerrufen', wert: k.einwilligung_widerrufen, filter: { einwilligung: 'widerrufen' } },
    },
    {
      id: 'sperren', titel: 'Blacklist und E-Mail',
      haupt: { label: 'auf der Blacklist', wert: k.blacklist, filter: { blacklist: 'true' } },
      teil: { label: 'ohne E-Mail', wert: k.ohne_email, filter: { ohne_email: 'true' } },
    },
  ];
}

/** aktiv = der Filter der Liste entspricht genau diesem Kachelwert (Personen ohne Filter nur, wenn gar kein Filter aktiv) */
export const istAktiv = (w: KpiWert, f: CrmFilter): boolean => gleich(w.filter, f);

/** Zugänglicher Name eines Kachelwerts */
export function wertAriaLabel(kachel: KpiKachel, w: KpiWert): string {
  const ziel = aktiveFilter(w.filter).length === 0 ? 'alle Personen anzeigen' : `Liste filtern: ${aktiveFilter(w.filter).map((k) => filterLabel(k, w.filter[k]!)).join(', ')}`;
  return `${kachel.titel}, ${w.label}: ${zahl(w.wert)}${kachel.hinweis ? ` (${kachel.hinweis})` : ''} — ${ziel}`;
}

export const AMPEL_LABEL: Record<CrmAmpel, string> = { gruen: 'Aktuell', gelb: 'Läuft', rot: 'Prüfen' };
const MODUS_LABEL: Record<string, string> = { full: 'Vollabgleich', delta: 'Delta', probe: 'Probe', dry_run: 'Probelauf' };
const LAUF_STATUS: Record<string, string> = { ok: 'erfolgreich', laeuft: 'läuft', fehler: 'Fehler' };
export const modusLabel = (m: string): string => MODUS_LABEL[m] ?? m;
export const laufStatusLabel = (s: string): string => LAUF_STATUS[s] ?? s;
export const zeitLabel = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '–' : ZEIT.format(d);
};
